import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import isabelPhoto from "@assets/team/isabel-milbourn.jpg";
import sierraPhoto from "@assets/team/sierra-richardson.jpg";
import alesiaPhoto from "@assets/team/alesia-dawkins.jpg";
import jenPhoto from "@assets/team/jen-gensits.jpg";
import amandaPhoto from "@assets/team/amanda-desousa.jpg";
import angelaPhoto from "@assets/team/angela-mageau.jpg";

const teamMembers = [
  {
    name: "Jen Gensits",
    photo: jenPhoto,
    teamLead: true,
    bio: "Hey, I'm Jen! I originally called California home, but Tallahassee has truly become my hometown and I wouldn't trade it. When I'm not working, I'm gardening, practicing yoga, or pampering my two cats — Quercus, a distinguished House Panther, and his sister Thais, a beautiful Siamese. Life is good!",
  },
  {
    name: "Amanda deSousa",
    photo: amandaPhoto,
    teamLead: true,
    bio: "Hey, I'm Amanda and I'm an Atlanta girl bringing that city energy to Tallahassee! When I'm not on the job, I'm traveling, working as a brand ambassador at events, or honestly — just sleeping and recharging! I'm the proud mom of one sweet dog named Forrest!",
  },
  {
    name: "Sierra Richardson",
    photo: sierraPhoto,
    bio: "Hi, I'm Sierra and I'm a New England girl — born and raised in Massachusetts! When I'm not caring for your fur babies, I work for FWC's Division of Marine Fisheries Management, because my love for animals goes way beyond the backyard. At home I'm a proud pet mom to two dogs, Marlin and Sunny!",
  },
  {
    name: "Alesia Dawkins",
    photo: alesiaPhoto,
    bio: "Hey there, I'm Alesia and I'm a proud Tallahassee native! When I'm not working, you'll catch me curled up with a good book. I share my home with three dogs — Mollie, Shorty, and Babbles — so I know firsthand what it means to love your pets like family!",
  },
  {
    name: "Isabel Milbourn",
    photo: isabelPhoto,
    bio: "Hey, I'm Isabel and I come to you all the way from beautiful Bar Harbor, Maine! I'm currently pursuing my PhD in music theory at Florida State University, so life is busy but I wouldn't have it any other way. At home I share my life with my adorable Pyrenees mix, who keeps me grounded through it all!",
  },
  {
    name: "Angela Mageau",
    photo: angelaPhoto,
    bio: "Hi, I'm Angela! I am originally from North Carolina and in my sixth year with Tallahassee Lassie! When I am not at work, I am usually teaching at my other job, I love teaching, the Littles! I currently have two Aussie's, and a couple of grand golden doodles!",
  },
];

const founders = [
  {
    name: "Carla",
    title: "Owner / Founder",
    photo: "https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/e0e4f2f0-1027-4e30-8550-45cc578831c8/IMG_4152_jpg.jpg",
    bio: "Carla returned to the Tallahassee area in 2014 where she was working in social work. After some soul searching, she made the decision to use her social work skills in the pet world as well as continue with the human side, their owners. Carla has over 20 years experience caring for animals and has owned a pet most of her life. She is currently mom to a pug and cat. Carla is certified in pet CPR and First Aid by the Pet Health Academy and is a member of Pet Sitters International and National Association of Professional Pet Sitters. She takes this knowledge and receives Continuing Education Units, along with providing training to her team. She also utilizes Pet Pro Hero and Fetch Find in teaching her team. She's also Regional Director for Florida Pet Services Association, Founding VP of North Florida Association of Pet Services, and has multiple volunteer commitments in Tallahassee.",
  },
  {
    name: "Belinda",
    title: "Pet Specialist / Community Management Coordinator",
    photo: "https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/31f9d6f9-a3a3-479a-99cc-55f2a3c616f3/IMG_4156_jpg.jpg",
    bio: "Belinda is a registered nurse and a licensed funeral director by profession. She treats all animals like her \"patients\", exhibiting the same kindness, love, and concern. She joined the team from the beginning, assisting with administrative duties and pet sitting, along with helping Jon and Carla launch the \"Active Retiree Program.\" Belinda loves dogs, but her favorite pets to care for are cats; a love she developed from an early age, living with a mother who had cats her entire life!",
  },
  {
    name: "Jon",
    title: "Pet Care Specialist / Schedule Watcher",
    photo: "https://images.squarespace-cdn.com/content/v1/61a9415d512d9d24f6cc2d59/1d5b1214-8310-4925-9ecd-b04a60d31bc9/jon+.jpg",
    bio: "Jon and Belinda spearheaded our active retiree program, giving retirees a chance to exercise, be engaged in the community, and have the joy that animal, unconditional love brings. Jon has years of experience with pets; we often refer to him as the \"Dog Whisperer\". Dogs who are shy and not so \"people friendly\" seem to love him!",
  },
];

export default function OurTeam() {
  return (
    <div className="py-16 md:py-24">
      <SEO
        title="Our Team | Professional Pet Sitters & Dog Walkers"
        description="Pet Sitters and Dog Walkers of Tallahassee Lassie Pet Care, A Pet Sitting and Dog Walking Service in Tallahassee"
        path="/our-team"
      />
      <div className="container mx-auto px-4">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-foreground mb-6">Team Tallahassee Lassie Pet Care</h1>
          </div>
        </Reveal>

        <div className="max-w-5xl mx-auto">
          <Reveal>
            <h2 className="text-3xl font-serif font-bold text-center mb-12">Founding Members</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 mb-20">
            {founders.map((member, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="group">
                  <div className="overflow-hidden mb-6 card-lift rounded-3xl">
                    <img 
                      src={member.photo}
                      alt={`${member.name}, ${member.title}`}
                      className="w-full aspect-[3/4] object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <h3 className="text-2xl font-serif font-bold">{member.name}</h3>
                  <p className="text-primary font-medium mb-3">{member.title}</p>
                  <p className="text-muted-foreground leading-relaxed">{member.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <h2 className="text-3xl font-serif font-bold text-center mb-12">Our Team</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-16 mb-20">
            {teamMembers.map((member, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="group flex flex-col">
                  {member.photo ? (
                    <div className="overflow-hidden mb-5 card-lift rounded-3xl bg-primary/5">
                      <img 
                        src={member.photo}
                        alt={member.name}
                        className="w-full aspect-[3/4] object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-[3/4] bg-primary/5 flex flex-col items-center justify-center gap-3 mb-5 rounded-3xl" >
                      <span className="text-6xl">🐾</span>
                      <span className="text-primary font-serif font-bold text-lg">Photo Coming Soon</span>
                    </div>
                  )}
                  <h3 className="text-2xl font-serif font-bold">{member.name}</h3>
                  {member.teamLead && (
                    <p className="text-primary font-medium mb-3">Team Lead</p>
                  )}
                  <p className={`text-muted-foreground leading-relaxed ${member.teamLead ? "" : "mt-3"}`}>{member.bio}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal>
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <div className="bg-secondary/5 border border-secondary/20 p-8 md:p-12" style={{ borderRadius: "24px 0 24px 0" }}>
              <div className="flex justify-center gap-1 mb-4 text-yellow-400">
                {[...Array(5)].map((_, i) => <Star key={i} fill="currentColor" size={24} />)}
              </div>
              <p className="text-xl font-serif font-bold text-foreground mb-4">
                Tallahassee Lassie was named the Best in Florida and Best of Next Door 2020, 2021, 2022!
              </p>
              <p className="text-lg text-muted-foreground italic">
                We offer a Peace of Mind Guarantee! After our Meet & Greet, you'll be assigned a primary sitter, a secondary backup, and an emergency back-up. We are proud to be the only sitter service in Tallahassee to be nationally certified, as well as fully bonded and insured!
              </p>
            </div>
            <Button asChild size="lg" className="h-14 px-8 text-lg mt-6">
              <Link href="/contact-us">
                Get Started Now
              </Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
